import React, { useState } from 'react'
import {
  Flex,
  Picker,
  NumberField,
  ActionButton,
  Text,
  Item,
  ProgressCircle
} from '@adobe/react-spectrum'

import allActions from '../config.json'
import actionWebInvoke from '../utils'

const CATEGORIES = {
  length: {
    label: 'Length',
    icon: '📏',
    color: '#1473E6',
    conversions: {
      miles_km: 'Miles → Kilometers',
      km_miles: 'Kilometers → Miles',
      feet_meters: 'Feet → Meters',
      meters_feet: 'Meters → Feet',
      inches_cm: 'Inches → Centimeters',
      cm_inches: 'Centimeters → Inches'
    }
  },
  weight: {
    label: 'Weight',
    icon: '⚖️',
    color: '#E63946',
    conversions: {
      lbs_kg: 'Pounds → Kilograms',
      kg_lbs: 'Kilograms → Pounds',
      oz_grams: 'Ounces → Grams',
      grams_oz: 'Grams → Ounces'
    }
  },
  temperature: {
    label: 'Temperature',
    icon: '🌡️',
    color: '#F4A940',
    conversions: {
      f_c: 'Fahrenheit → Celsius',
      c_f: 'Celsius → Fahrenheit',
      c_k: 'Celsius → Kelvin',
      k_c: 'Kelvin → Celsius'
    }
  }
}

const actions = Object.keys(allActions).reduce((obj, key) => {
  if (key.lastIndexOf('/') > -1) { obj[key] = allActions[key] }
  return obj
}, {})

const actionName = Object.keys(actions)[0]

export const Home = () => {
  const [category, setCategory] = useState(null)
  const [conversion, setConversion] = useState(null)
  const [value, setValue] = useState(0)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)

  const conversions = category ? CATEGORIES[category].conversions : {}
  const catMeta = category ? CATEGORIES[category] : null

  async function handleConvert () {
    if (!category || !conversion || !actionName) return
    setLoading(true)
    try {
      const result = await actionWebInvoke(actions[actionName], {}, { value, conversion, category })
      setHistory(prev => [{
        id: Date.now().toString(),
        input: `${result.input}`,
        output: `${result.output}`,
        label: result.conversion,
        category,
        color: CATEGORIES[category].color,
        icon: CATEGORIES[category].icon
      }, ...prev].slice(0, 20))
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  function removeRow (id) {
    setHistory(prev => prev.filter(item => item.id !== id))
  }

  return (
    <div className="uc-root">
      <div className="uc-card">
        <div className="uc-header">
          <div className="uc-logo">⇄</div>
          <div>
            <h1 className="uc-title">Unit Converter</h1>
            <p className="uc-subtitle">Sample application built using App Builder and Agent Skills</p>
          </div>
        </div>

        <div className="uc-categories">
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              className={`uc-cat-btn ${category === key ? 'active' : ''}`}
              style={{ '--cat-color': cat.color }}
              onClick={() => { setCategory(key); setConversion(null) }}
            >
              <span className="uc-cat-icon">{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {category && (
          <div className="uc-form">
            <Picker
              label="Conversion"
              placeholder="Select a conversion"
              selectedKey={conversion}
              onSelectionChange={setConversion}
              items={Object.entries(conversions).map(([k, v]) => ({ id: k, name: v }))}
              width="100%"
            >
              {(item) => <Item key={item.id}>{item.name}</Item>}
            </Picker>

            <Flex gap="size-200" alignItems="end">
              <NumberField label="Value" value={value} onChange={setValue} width="100%" />
              <ActionButton
                onPress={handleConvert}
                isDisabled={!conversion || loading}
                UNSAFE_style={{
                  background: catMeta.color,
                  color: '#fff',
                  borderColor: catMeta.color,
                  borderRadius: '8px',
                  fontWeight: 700,
                  minWidth: '100px'
                }}
              >
                {loading ? <ProgressCircle aria-label="Converting..." isIndeterminate size="S" /> : <Text>Convert</Text>}
              </ActionButton>
            </Flex>
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="uc-card uc-results">
          <div className="uc-results-header">
            <h2 className="uc-results-title">Results</h2>
            <button className="uc-clear-btn" onClick={() => setHistory([])}>Clear all</button>
          </div>
          <div className="uc-result-list">
            {history.map((item) => (
              <div key={item.id} className="uc-result-row" style={{ '--row-color': item.color }}>
                <span className="uc-result-icon">{item.icon}</span>
                <div className="uc-result-info">
                  <span className="uc-result-label">{item.label}</span>
                  <span className="uc-result-values">
                    <span className="uc-result-input">{item.input}</span>
                    <span className="uc-result-arrow">→</span>
                    <span className="uc-result-output">{item.output}</span>
                  </span>
                </div>
                <button className="uc-delete-btn" onClick={() => removeRow(item.id)} title="Remove">×</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
